import type { ReactNode } from 'react';
import type { LoSiteBlock } from '@/lib/lyrraone-site-blocks';
import type { LoSiteHeroLayout } from '@/lib/lyrraone-site-theme-profiles';
import type { LoSiteElementStyle } from '@/lib/lyrraone-site-elements';
import { resolveLoSiteLink } from '@/lib/lyrraone-site-links';
import { resolveLoBlockLink, type LoBlockLinkContext } from '@/lib/lyrraone-block-links';
import {
  buttonStyleToCss,
  elementStyleClass,
  elementStyleToCss,
  hasCustomButtonStyle,
} from '@/lib/lyrraone-site-elements';
import { SiteThemeLink, ThemeButtonText, ThemeEditable } from '@/components/lyrraone/site-themes/LoSiteThemeEditable';
import { LoSiteHeroBackground } from '@/components/lyrraone/LoSiteHeroBackground';
import { heroHasBackgroundMedia } from '@/lib/lyrraone-site-hero-media';
import { resolveSiteImageUrl } from '@/components/common/ImageSourcePicker';
import { cn } from '@/lib/utils';

interface LoSiteThemedHeroProps {
  layout: LoSiteHeroLayout;
  block: LoSiteBlock;
  blockKey: string;
  siteSlug: string;
  customDomainSlug?: string | null;
  editMode?: boolean;
  selectedElementKey?: string | null;
  onSelectElement?: (key: string) => void;
  onInlineTextChange?: (key: string, value: string) => void;
  styleOf: (key: string) => LoSiteElementStyle | undefined;
  linkContext?: LoBlockLinkContext;
}

function HeroCta({
  block,
  siteSlug,
  customDomainSlug,
  editMode,
  selectedElementKey,
  onSelectElement,
  onInlineTextChange,
  styleOf,
  linkContext,
}: Omit<LoSiteThemedHeroProps, 'layout' | 'blockKey'>) {
  if (!block.ctaText || !block.ctaUrl) return null;
  const href = linkContext
    ? resolveLoBlockLink(block.ctaUrl, linkContext)
    : resolveLoSiteLink(siteSlug, block.ctaUrl, customDomainSlug);
  return (
    <div className="lo-st-hero__actions">
      <ThemeEditable
        elementKey="hero.cta"
        editMode={editMode}
        selectedElementKey={selectedElementKey}
        onSelectElement={onSelectElement}
        as="span"
        className="inline-block"
      >
        <SiteThemeLink
          href={href}
          className={cn('lo-btn lo-btn-primary lo-st-btn', hasCustomButtonStyle(styleOf('hero.cta')) && 'lo-btn-custom')}
          style={buttonStyleToCss(styleOf('hero.cta'))}
          editMode={editMode}
          onClick={() => onSelectElement?.('hero.cta')}
        >
          <ThemeButtonText
            elementKey="hero.cta"
            editMode={editMode}
            selectedElementKey={selectedElementKey}
            onSelectElement={onSelectElement}
            onInlineTextChange={onInlineTextChange}
          >
            {block.ctaText}
          </ThemeButtonText>
        </SiteThemeLink>
      </ThemeEditable>
    </div>
  );
}

function HeroTitleSubtitle(props: Omit<LoSiteThemedHeroProps, 'layout' | 'blockKey'>) {
  const { block, editMode, selectedElementKey, onSelectElement, onInlineTextChange, styleOf } = props;
  return (
    <>
      {block.title && (
        <ThemeEditable
          elementKey="hero.title"
          editMode={editMode}
          selectedElementKey={selectedElementKey}
          onSelectElement={onSelectElement}
          onInlineTextChange={onInlineTextChange}
          className={cn('lo-st-hero__title', elementStyleClass(styleOf('hero.title')))}
          style={elementStyleToCss(styleOf('hero.title'))}
          as="h1"
          editableText
        >
          {block.title}
        </ThemeEditable>
      )}
      {block.subtitle && (
        <ThemeEditable
          elementKey="hero.subtitle"
          editMode={editMode}
          selectedElementKey={selectedElementKey}
          onSelectElement={onSelectElement}
          onInlineTextChange={onInlineTextChange}
          className={cn('lo-st-hero__lead', elementStyleClass(styleOf('hero.subtitle')))}
          style={elementStyleToCss(styleOf('hero.subtitle'))}
          as="p"
          editableText
        >
          {block.subtitle}
        </ThemeEditable>
      )}
    </>
  );
}

function HeroVisual({ block }: { block: LoSiteBlock }) {
  const sideImage = resolveSiteImageUrl(block.imageUrl);
  if (sideImage) {
    return (
      <div className="lo-st-hero__visual" aria-hidden>
        <img src={sideImage} alt="" className="lo-st-hero__side-image" />
      </div>
    );
  }
  return (
    <div className="lo-st-hero__visual" aria-hidden>
      <div className="lo-st-hero__visual-inner">
        <div className="lo-st-hero__shape lo-st-hero__shape--a" />
        <div className="lo-st-hero__shape lo-st-hero__shape--b" />
      </div>
    </div>
  );
}

function HeroSection({
  blockKey,
  className,
  block,
  children,
  decor = 'none',
}: {
  blockKey: string;
  className: string;
  block: LoSiteBlock;
  children: ReactNode;
  decor?: 'mesh' | 'glow' | 'none';
}) {
  const hasBg = heroHasBackgroundMedia(block);
  return (
    <section key={blockKey} className={cn(className, hasBg && 'lo-st-hero--has-bg-media')}>
      <LoSiteHeroBackground block={block} />
      {!hasBg && decor === 'mesh' && <div className="lo-st-hero__mesh" aria-hidden />}
      {!hasBg && decor === 'glow' && <div className="lo-st-hero__glow" aria-hidden />}
      {children}
    </section>
  );
}

export function LoSiteThemedHero({
  layout,
  block,
  blockKey,
  siteSlug,
  customDomainSlug,
  editMode,
  selectedElementKey,
  onSelectElement,
  onInlineTextChange,
  styleOf,
  linkContext,
}: LoSiteThemedHeroProps) {
  const shared = {
    block,
    siteSlug,
    customDomainSlug,
    editMode,
    selectedElementKey,
    onSelectElement,
    onInlineTextChange,
    styleOf,
    linkContext,
  };

  /** Image latérale fournie → composition split, même si le thème est « centered » (ex. formulaires). */
  const hasSideImage = Boolean(resolveSiteImageUrl(block.imageUrl));
  const useSplit = layout === 'split' || hasSideImage;

  if (useSplit) {
    return (
      <HeroSection blockKey={blockKey} block={block} className="lo-st-hero lo-st-hero--split">
        <div className="lo-st-hero__grid lo-wrap lo-st-hero__content-layer">
          <div className="lo-st-hero__content">
            <HeroTitleSubtitle {...shared} />
            <HeroCta {...shared} />
          </div>
          <HeroVisual block={block} />
        </div>
      </HeroSection>
    );
  }

  if (layout === 'fullscreen') {
    return (
      <HeroSection blockKey={blockKey} block={block} className="lo-st-hero lo-st-hero--fullscreen" decor="mesh">
        <div className="lo-st-hero__inner lo-wrap lo-st-hero__inner--center lo-st-hero__content-layer">
          <HeroTitleSubtitle {...shared} />
          <HeroCta {...shared} />
        </div>
      </HeroSection>
    );
  }

  if (layout === 'editorial') {
    return (
      <HeroSection blockKey={blockKey} block={block} className="lo-st-hero lo-st-hero--editorial">
        <div className="lo-wrap lo-st-hero__content-layer">
          <p className="lo-st-hero__kicker">Bienvenue</p>
          <HeroTitleSubtitle {...shared} />
          <HeroCta {...shared} />
        </div>
      </HeroSection>
    );
  }

  if (layout === 'minimal') {
    return (
      <HeroSection blockKey={blockKey} block={block} className="lo-st-hero lo-st-hero--minimal">
        <div className="lo-wrap lo-st-hero__content-layer">
          <HeroTitleSubtitle {...shared} />
          <HeroCta {...shared} />
        </div>
      </HeroSection>
    );
  }

  if (layout === 'bold') {
    return (
      <HeroSection blockKey={blockKey} block={block} className="lo-st-hero lo-st-hero--bold" decor="glow">
        <div className="lo-wrap lo-st-hero__inner--center lo-st-hero__content-layer">
          <span className="lo-st-hero__badge">Formation en ligne</span>
          <HeroTitleSubtitle {...shared} />
          <HeroCta {...shared} />
        </div>
      </HeroSection>
    );
  }

  /* centered — défaut type SaaS / WordPress Astra */
  return (
    <HeroSection blockKey={blockKey} block={block} className="lo-st-hero lo-st-hero--centered">
      <div className="lo-wrap lo-st-hero__inner--center lo-st-hero__content-layer">
        <HeroTitleSubtitle {...shared} />
        <HeroCta {...shared} />
      </div>
    </HeroSection>
  );
}
